import React, { useState } from 'react';
import './CreateJobAdvanced.scss';
import { createJobConfigHooks } from './hooks';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Checkbox,
  FormGroup,
} from '@mui/material';
import { JobDetailsCard } from './components/JobDetailsCard';
import { NotificationsCard } from './components/NotificationsCard';
import { ScheduleCard } from './components/ScheduleCard';
import { MappingTableCard } from './components/MappingTableCard';

// Mirrors charlotte-jobs/src/mep-export/schema.ts `defaultFileNamingSchema`.
// {PLAN_ID} and the date tokens are substituted server-side, not here.
const DEFAULT_FILE_NAMING_SCHEMA =
  '/migrated/wurk/WURK_{PLAN_ID}_{YEAR_4_DIGITS}{MONTH_LEADING_ZERO}{DAY_LEADING_ZERO}_{HOUR_24}{MINUTE}{SECOND}.csv';

const DEFAULT_CONFIG = {
  kronos: {
    type: '1password_item',
    '1password_item_path': 'Client credentials/UKG - EnjoyWurkAdmin - apiuser',
  },
  sftp: {
    type: '1password_item',
    '1password_item_path': 'Client credentials/SFTP PCS',
  },
  emailConfig: {
    emailContacts: [],
    sendSuccessEmails: 'true',
  },
  reportDetails: {
    reportID: '',
    planID: '',
  },
  mapping: {},
  matchType: 'generic',
  // fileNamingSchema is intentionally absent by default: the job runner falls
  // back to defaultFileNamingSchema (mirrored here as DEFAULT_FILE_NAMING_SCHEMA)
  // when it's unset, so we only write it once the override is actually enabled.
};

const ClientPlanCard = ({ useJobConfig }) => {
  const [companyName, setCompanyName] = useJobConfig(['companyName']);
  const [reportID, setReportID] = useJobConfig(['reportDetails', 'reportID']);
  const [planID, setPlanID] = useJobConfig(['reportDetails', 'planID']);
  return (
    <Card>
      <CardHeader title="Client & Plan" subheader="Identifies which client and 401(k) plan this feed is for" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Company short name"
              variant="standard"
              helperText="Internal client identifier"
              required
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Kronos report ID"
              variant="standard"
              helperText="Saved report ID with the payroll deductions"
              required
              value={reportID}
              onChange={(e) => setReportID(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Plan ID"
              variant="standard"
              helperText="e.g. ITC001"
              required
              value={planID}
              onChange={(e) => setPlanID(e.target.value)}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const OutputOptionsCard = ({ useJobConfig, useJobConfigPatch }) => {
  const [matchType, setMatchType] = useJobConfig(['matchType']);
  const [config, patchConfig] = useJobConfigPatch();
  const splitMode = config.splitByEIN ? 'ein' : config.splitByPayPeriod ? 'payPeriod' : 'single';
  const setSplitMode = (mode) => {
    patchConfig({
      splitByEIN: mode === 'ein' ? true : undefined,
      splitByPayPeriod: mode === 'payPeriod' ? true : undefined,
    });
  };

  return (
    <Card>
      <CardHeader title="Output options" subheader="How the 401(k) contribution file is generated" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel>Match type</FormLabel>
              <RadioGroup row value={matchType || 'generic'} onChange={(e) => setMatchType(e.target.value)}>
                <FormControlLabel value="generic" control={<Radio />} label="Generic 401(k)" />
                <FormControlLabel value="employer" control={<Radio />} label="Employer match" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel>File split</FormLabel>
              <RadioGroup value={splitMode} onChange={(e) => setSplitMode(e.target.value)}>
                <FormControlLabel
                  value="single"
                  control={<Radio />}
                  label={<>Single file<FormHelperText>One file with all employees</FormHelperText></>}
                />
                <FormControlLabel
                  value="ein"
                  control={<Radio />}
                  label={<>One file per EIN<FormHelperText>Some clients have separate funding accounts per EIN</FormHelperText></>}
                />
                <FormControlLabel
                  value="payPeriod"
                  control={<Radio />}
                  label={<>One file per pay period<FormHelperText>Splits output by pay period frequency</FormHelperText></>}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const AdvancedCard = ({ useJobConfig }) => {
  const [fileNamingSchema, setFileNamingSchema] = useJobConfig(['fileNamingSchema']);
  const [custom, setCustom] = useState(fileNamingSchema !== undefined);

  return (
    <Card>
      <CardHeader title="Advanced / Experimental" subheader="Rarely needed" />
      <CardContent>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox />}
            checked={custom}
            onChange={(e, checked) => {
              setCustom(checked);
              setFileNamingSchema(checked ? DEFAULT_FILE_NAMING_SCHEMA : undefined);
            }}
            label={
              <>
                Override file naming schema
                <FormHelperText>
                  Defaults to <code>{DEFAULT_FILE_NAMING_SCHEMA}</code>. Only change this if the vendor requests a different pattern.
                </FormHelperText>
              </>
            }
          />
          <TextField
            fullWidth
            label="File naming schema"
            variant="standard"
            value={fileNamingSchema || DEFAULT_FILE_NAMING_SCHEMA}
            onChange={(e) => setFileNamingSchema(e.target.value)}
            disabled={!custom}
          />
        </FormGroup>
      </CardContent>
    </Card>
  );
};

const ExportFormBody = ({ partner, defaultConfig }) => {
  const { useJobGeneralConfig, useJobConfig, useJobConfigPatch } = createJobConfigHooks({ partner, defaultConfig });
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <JobDetailsCard useJobGeneralConfig={useJobGeneralConfig} />
      </Grid>
      <Grid item xs={12}>
        <ClientPlanCard useJobConfig={useJobConfig} />
      </Grid>
      <Grid item xs={12}>
        <MappingTableCard
          title="EIN → Division mapping"
          subheader="Maps each Employee EIN to its Division code used in the export. EINs not listed default to using the EIN itself as the division code."
          keyColumnLabel="Employee EIN"
          valueColumnLabel="Division code"
          keyPlaceholder="e.g. EIN5555"
          valuePlaceholder="e.g. P1"
          useJobConfig={useJobConfig}
          fieldPath={['mapping']}
        />
      </Grid>
      <Grid item xs={12}>
        <OutputOptionsCard useJobConfig={useJobConfig} useJobConfigPatch={useJobConfigPatch} />
      </Grid>
      <Grid item xs={12}>
        <NotificationsCard useJobConfig={useJobConfig} />
      </Grid>
      <Grid item xs={12}>
        <ScheduleCard useJobGeneralConfig={useJobGeneralConfig} />
      </Grid>
      <Grid item xs={12}>
        <AdvancedCard useJobConfig={useJobConfig} />
      </Grid>
    </Grid>
  );
};

export const MepExportForm = () => <ExportFormBody partner="PCS" defaultConfig={DEFAULT_CONFIG} />;
export const PcsExportForm = () => <ExportFormBody partner="PCS" defaultConfig={DEFAULT_CONFIG} />;
